import {useBreakpointUp} from '@travel-frontend/core';
import {IconArrowForward, IconCheckCircle} from '@travel-frontend/icons';
import {
  Button,
  HeadlineLarge,
  LabelLargeSemibold,
  LabelMediumSemiBold,
  ParagraphLarge,
  ParagraphMedium,
  ParagraphSmall,
} from '@travel-frontend/ui-components';
import {useTranslation, Trans} from 'react-i18next';

import inviaLogo from '@/assets/images/cross-border-invia-logo.svg';
import {Banner} from '@/components/CrossBorderBannerV2/components/Banner';
import {Chip} from '@/components/CrossBorderBannerV2/components/Chip';
import {Chips} from '@/components/CrossBorderBannerV2/components/Chips';
import {Container} from '@/components/CrossBorderBannerV2/components/Container';
import {InnerLayout} from '@/components/CrossBorderBannerV2/components/InnerLayout';
import {Subtitle} from '@/components/CrossBorderBannerV2/components/Subtitle';
import {Title} from '@/components/CrossBorderBannerV2/components/Title';

type Props = Omit<React.ComponentPropsWithoutRef<typeof Container>, 'size'> & {
  airports: Array<string>;
  url: string;
};

export const CrossBorderBannerV2 = ({airports, url, ...restProps}: Props) => {
  const {t} = useTranslation('crossBorder');

  const mdUp = useBreakpointUp('md');
  const lgUp = useBreakpointUp('lg');
  // eslint-disable-next-line sonarjs/no-nested-conditional -- really?
  const size = lgUp ? 'large' : mdUp ? 'medium' : 'small';
  const TitleComponent = {
    small: LabelMediumSemiBold,
    medium: LabelLargeSemibold,
    large: HeadlineLarge,
  }[size];
  const SubtitleComponent = {
    small: ParagraphSmall,
    medium: ParagraphMedium,
    large: ParagraphLarge,
  }[size];

  return (
    <Container
      {...restProps}
      href={url}
      target="_blank"
      rel="noreferrer"
      size={size}
    >
      <Banner size={size}>
        <img src={inviaLogo} alt="" />
      </Banner>
      <Title as={TitleComponent} size={size}>
        <span>{t('teaser') + ' '}</span>
        {t('title', {airports})}
      </Title>
      <Subtitle as={SubtitleComponent}>
        <Trans
          ns="crossBorder"
          i18nKey="subtitle"
          components={{
            partner: <span />,
          }}
        />
      </Subtitle>
      <InnerLayout size={size}>
        <Chips gap={mdUp ? 'medium' : 'small'}>
          <li>
            <Chip size={mdUp ? 'medium' : 'small'}>
              <IconCheckCircle fontSize="1rem" />
              {t('benefit1')}
            </Chip>
          </li>
          <li>
            <Chip size={mdUp ? 'medium' : 'small'}>
              <IconCheckCircle fontSize="1rem" />
              {t('benefit2')}
            </Chip>
          </li>
          <li>
            <Chip size={mdUp ? 'medium' : 'small'}>
              <IconCheckCircle fontSize="1rem" />
              {t('benefit3')}
            </Chip>
          </li>
        </Chips>
        <Button
          as="span"
          icon={<IconArrowForward />}
          iconPosition="right"
          style={{
            marginLeft: 'auto',
            boxShadow:
              '0px 8px 24px 0px rgba(0, 0, 0, 0.1), 0px 0px 24px 0px rgba(0, 0, 0, 0.08)',
          }}
        >
          {t('cta', {context: 'without_price'})}
        </Button>
      </InnerLayout>
    </Container>
  );
};
